import React, { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import api from '@/services/api'
import {
  Avatar, Snackbar, Alert, Chip, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Box, Typography, Paper, Stack, Grid, IconButton, alpha,
  useTheme, useMediaQuery, Select, MenuItem, FormControl,
} from '@mui/material'
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety'
import RoomIcon from '@mui/icons-material/Room'
import { ArrowBack, Person } from '@mui/icons-material'
import InfoIcon from '@mui/icons-material/Info'
import CloseIcon from '@mui/icons-material/Close'
import ScheduleIcon from '@mui/icons-material/Schedule'
import DescriptionIcon from '@mui/icons-material/Description'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import LanguageIcon from '@mui/icons-material/Language'
import PaymentIcon from '@mui/icons-material/Payment'
import { useAppSelector } from '@/store'
import { UserRole } from '@/types'
import TherapistBooking from '../TherapistBooking'
import { MainLayout } from '@/components/layout/MainLayout'
import AdvancedPagination from '@components/pagination/AdvancedPagination'
import SignupHeader from './SignupHeader'
import PersonIcon from '@mui/icons-material/Person'
import AgencyBooking from '../AgencyBooking'

type Tab = 'overview' | 'providers' | 'services' | 'reviews'

const OpenAgencyPage: React.FC = () => {
  const { agencyId } = useParams<{ agencyId: string }>()
  const navigate = useNavigate()
  const routerLocation = useLocation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const { user, isAuthenticated } = useAppSelector(s => s.auth)
  const isPatient = isAuthenticated && user?.role === UserRole.CLIENT

  const [agency, setAgency] = useState<any>(null)
  const [agencyServices, setAgencyServices] = useState<any[]>([])
  const [perSession, setPerSession] = useState<string>('')
  const [providers, setProviders] = useState<any[]>([])
  const [totalProviders, setTotalProviders] = useState(0)
  const [providerPage, setProviderPage] = useState(0)
  const [providerRowsPerPage, setProviderRowsPerPage] = useState(10)
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState<Tab>(
    (routerLocation.state as any)?.tab ?? 'overview'
  )
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' as 'error' | 'success' })

  // booking state
  const [showBooking, setShowBooking] = useState(false)
  const [selectedProvider, setSelectedProvider] = useState<any>(null)
  const [providerServices, setProviderServices] = useState<any[]>([])
  const [servicesLoading, setServicesLoading] = useState(false)
  const [selectedPackage, setSelectedPackage] = useState<any>(null)

  // specialty filter
  const [selectedSpecialty, setSelectedSpecialty] = useState<string>('')
  const [specialtyOptions, setSpecialtyOptions] = useState<{ id: string; name: string }[]>([])

  // package details dialog
  const [packageDetailsOpen, setPackageDetailsOpen] = useState(false)

  // login required dialog
  const [loginDialogOpen, setLoginDialogOpen] = useState(false)

  useEffect(() => {
    if (agencyId) fetchAgency()
  }, [agencyId])

  const fetchAgency = async () => {
    try {
      setLoading(true)
      const res = await api.get(`/agency/get-agency/${agencyId}`)
      setAgency(res.data?.data || res.data)
      setAgencyServices(res.data?.services || [])
      setPerSession(res.data?.per_session || '')
      // fetch first page for display + all for specialty options in parallel
      fetchProviders(0, providerRowsPerPage, '')
      fetchSpecialtyOptions()
    } catch {
      setSnackbar({ open: true, message: 'Unable to load agency details.', severity: 'error' })
      setLoading(false)
    }
  }

  // one-time fetch to populate specialty dropdown options from all providers
  const fetchSpecialtyOptions = async () => {
    try {
      const res = await api.get(`/agency-provider/agency-providers`, {
        params: { agencyId, page: 1, limit: 200 },
      })
      const all: any[] = res.data?.data || []
      const map = new Map<string, string>()
      all.forEach(p => {
        p.specialization?.forEach((s: any) => {
          if (s._id && s.name) map.set(s._id, s.name)
        })
      })
      setSpecialtyOptions(Array.from(map.entries()).map(([id, name]) => ({ id, name })))
    } catch {
      // silently fail — dropdown just stays empty
    }
  }

  const fetchProviders = useCallback(async (page = 0, limit = 10, specialtyId = '') => {
    try {
      const params: Record<string, any> = { agencyId, page: page + 1, limit }
      if (specialtyId) params.specialization = specialtyId
      const res = await api.get(`/agency-provider/agency-providers`, { params })
      const results: any[] = res.data?.data || []
      const total: number = res.data?.totalRecords ?? results.length
      setProviders(results)
      setTotalProviders(total)
      if (page === 0 && results.length > 0) {
        setSelectedProvider(results[0])
        fetchProviderServices(results[0]._id)
      }
    } catch {
      // providers optional
    } finally {
      setLoading(false)
    }
  }, [agencyId])

  const fetchProviderServices = async (therapistId: string) => {
    if (!therapistId || !agencyId) return
    try {
      setServicesLoading(true)
      setSelectedPackage(null)
      const res = await api.get(`/contracts/all-services/${agencyId}/${therapistId}`)
      const svcs: any[] = res.data?.data || res.data?.results || res.data || []
      setProviderServices(Array.isArray(svcs) ? svcs : [])
    } catch {
      setProviderServices([])
    } finally {
      setServicesLoading(false)
    }
  }

  const handleProviderSelect = (provider: any) => {
    setSelectedProvider(provider)
    setSelectedPackage(null)
    fetchProviderServices(provider?._id)
  }

  const handleSpecialtyChange = (specialtyId: string) => {
    setSelectedSpecialty(specialtyId)
    setProviderPage(0)
    fetchProviders(0, providerRowsPerPage, specialtyId)
  }

  const handleProviderPageChange = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setProviderPage(newPage)
    fetchProviders(newPage, providerRowsPerPage, selectedSpecialty)
  }

  const handleProviderRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const newLimit = parseInt(event.target.value, 10)
    setProviderRowsPerPage(newLimit)
    setProviderPage(0)
    fetchProviders(0, newLimit, selectedSpecialty)
  }

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="w-12 h-12 mx-auto border-4 border-green-300 rounded-full border-t-green-600 animate-spin" />
            <p className="mt-4 text-gray-600">Loading agency...</p>
          </div>
        </div>
      </MainLayout>
    )
  }

  if (!agency) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center min-h-screen">
          <p className="text-gray-600">
            Agency not found.{' '}
            <button onClick={() => navigate(-1)} className="text-[#3BAE77] hover:underline">
              Go back
            </button>
          </p>
        </div>
      </MainLayout>
    )
  }

  // agency fields from actual response
  const agencyName = agency.agencyName || agency.name || 'Agency'
  const photoUrl = agency.profilePhotoUrl || agency.logoUrl
  const insurances: any[] = agency.insuranceAccepted || []
  const noOfProvider = agency.noOfProvider ?? providers.length
  const specializations: any[] = agency.specialization || []
  const address = [agency.addressLine1, agency.city, agency.state, agency.zipCode, agency.country]
    .filter(Boolean)
    .join(', ')
  const bio = agency.bio || agency.description || ''

  const tabs: { key: Tab; label: string }[] = [
    { key: 'overview', label: 'Overview' },
    { key: 'providers', label: `Providers (${totalProviders || providers.length})` },
    { key: 'services', label: `Services${agencyServices.length ? ` (${agencyServices.length})` : ''}` },
    { key: 'reviews', label: 'Reviews' },
  ]

  const providerName = selectedProvider
    ? `${selectedProvider.firstName || ''} ${selectedProvider.lastName || ''}`.trim()
    : ''

  // service fee field is private_pay_rate (not sessionFee)
  const svcFee = (svc: any) => svc?.private_pay_rate ?? svc?.sessionFee ?? 0

  const ProviderCard = ({ item }: { item: any }) => {
    if (!item?._id) return null
    const name = `${item.firstName || ''} ${item.lastName || ''}`.trim()
    const exp = item.yearsOfExperience ? `${item.yearsOfExperience} years of experience` : ''
    const specs: string[] = item.specialization?.map((s: any) => s.name) || []
    const isSelected = selectedProvider?._id === item._id
    return (
      <div
        className={`flex gap-4 p-4 border rounded-2xl transition-colors ${isSelected ? 'border-[#3BAE77] bg-green-50' : 'border-gray-200 bg-white hover:bg-gray-50'
          }`}
      >
        {/* Photo */}
        {item.profilePhotoUrl ? (
          <img
            src={item.profilePhotoUrl}
            alt={name}
            className="w-[120px] h-[140px] rounded-xl object-cover shrink-0"
          />
        ) : (
          <div className="w-[120px] h-[140px] rounded-xl bg-[#e8f5ee] flex items-center justify-center shrink-0">
            <Person sx={{ color: '#3BAE77', fontSize: 52 }} />
          </div>
        )}

        {/* Info */}
        <div className="flex flex-col flex-1 min-w-0 justify-between">
          <div>
            <span className="text-xs text-[#3BAE77] font-semibold flex items-center gap-1">
              Verified details ✓
            </span>
            <p className="font-bold text-gray-900 text-lg mt-1 leading-tight">{name}</p>
            {exp && (
              <span className="inline-block mt-2 px-3 py-0.5 rounded-full border border-[#3BAE77] text-[#3BAE77] text-xs font-medium bg-transparent">
                {exp}
              </span>
            )}
            {specs.length > 0 && (
              <p className="text-xs text-gray-500 mt-2 flex items-center gap-1">
                <HealthAndSafetyIcon sx={{ fontSize: 14, color: '#3BAE77' }} />
                {specs.slice(0, 3).join(', ')}
                {specs.length > 3 && (
                  <span className="font-semibold text-gray-700"> + {specs.length - 3} more</span>
                )}
              </p>
            )}
          </div>

          {/* Buttons */}
          <div className="flex items-center gap-3 mt-4">
            {isSelected ? (
              <button className="flex items-center gap-1.5 px-5 py-2 bg-[#1a6b3c] text-white rounded-xl text-sm font-semibold cursor-default">
                ✓ Selected
              </button>
            ) : (
              <button
                onClick={() => { handleProviderSelect(item); setShowBooking(false) }}
                className="px-5 py-2 bg-gray-800 text-white rounded-xl text-sm font-semibold hover:bg-gray-900 transition-colors cursor-pointer"
              >
                Select &amp; Book
              </button>
            )}
            <button
              onClick={() =>
                navigate(isPatient ? `/therapists-details/${item._id}` : `/therapists-profile-details/${item._id}`, { state: { fromAgency: true, agencyId } })
              }
              className="px-5 py-2 border border-gray-300 text-gray-700 rounded-xl text-sm font-semibold hover:bg-gray-100 transition-colors cursor-pointer"
            >
              View Profile ↗
            </button>
          </div>
        </div>
      </div>
    )
  }


  console.log("agencyServices", agencyServices)

  return (
    <MainLayout>
      {!isAuthenticated && <SignupHeader />}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <div className="min-h-screen bg-gray-50 pb-10">
        {/* Back */}
        <div className="px-6 pt-6 max-w-7xl mx-auto">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 transition-colors mb-4"
          >
            <ArrowBack sx={{ fontSize: '1rem' }} />
            Back to previous page
          </button>
        </div>

        <div className="max-w-7xl mx-auto px-6 flex flex-col lg:flex-row gap-6">
          {/* Left column */}
          <div className="flex-1 min-w-0">
            {/* Agency header card */}
            <div className="bg-white rounded-2xl shadow-md overflow-hidden mb-6">
              <div className="relative bg-gradient-to-br from-green-50 to-green-100 p-6">
                <div className="flex gap-6 items-start">
                  {/* Logo */}
                  <div className="w-36 h-32 bg-white rounded-xl flex items-center justify-center shrink-0 border border-green-200 overflow-hidden shadow-sm">
                    {photoUrl ? (
                      <img src={photoUrl} alt={agencyName} className="w-full h-full object-contain p-2" />
                    ) : (
                      <div className="text-center p-3">
                        <span className="text-[#3BAE77] font-extrabold text-base leading-tight break-words">
                          {agencyName}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <h1 className="text-2xl font-bold text-gray-900 mb-3">{agencyName}</h1>

                    <p className="text-sm text-gray-600 mb-2 flex items-center gap-1.5">
                      <span>👥</span>
                      {noOfProvider} Providers
                    </p>

                    {specializations.length > 0 && (
                      <p className="text-sm text-gray-600 mb-2 flex items-center gap-1.5">
                        🏥 {specializations.map((s: any) => s.name).join(', ')}
                      </p>
                    )}

                    {insurances.length > 0 && (
                      <p className="text-sm text-gray-600 mb-2 flex items-center gap-1.5">
                        <HealthAndSafetyIcon sx={{ fontSize: 16, color: '#3BAE77' }} />
                        Accepts:{' '}
                        {insurances
                          .slice(0, 3)
                          .map((i: any) => i.payer_name)
                          .join(', ')}
                        {insurances.length > 3 && ` and ${insurances.length - 3} more`}
                      </p>
                    )}

                    {address && (
                      <p className="text-sm text-gray-600 mb-2 flex items-center gap-1.5">
                        <RoomIcon sx={{ fontSize: 16, color: '#3BAE77' }} />
                        {address}
                      </p>
                    )}

                    {perSession && (
                      <div className="mt-4">
                        <p className="text-gray-700 text-sm">
                          <span className="text-[#3BAE77] font-bold text-lg">{perSession}</span>
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="bg-white rounded-2xl shadow-md overflow-hidden">
              <div className="flex border-b border-gray-200 overflow-x-auto">
                {tabs.map(t => (
                  <button
                    key={t.key}
                    onClick={() => setActiveTab(t.key)}
                    className={`px-6 py-4 text-sm font-medium transition-colors border-b-2 -mb-px whitespace-nowrap cursor-pointer ${activeTab === t.key
                      ? 'border-[#3BAE77] text-[#3BAE77]'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                      }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>

              <div className="p-6">
                {/* Overview */}
                {activeTab === 'overview' && (
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900 mb-3">About {agencyName}</h2>
                    {bio ? (
                      <p className="text-gray-600 leading-relaxed whitespace-pre-wrap">{bio}</p>
                    ) : (
                      <p className="text-gray-500 italic">No description available.</p>
                    )}

                    {providers.length > 0 && (
                      <div className="mt-8">
                        <h2 className="text-lg font-semibold text-gray-900 mb-4">
                          Providers in {agencyName}
                        </h2>
                        <div className="space-y-3">
                          {providers.slice(0, 3).map((item: any) => (
                            <ProviderCard key={item._id} item={item} />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Providers tab */}
                {activeTab === 'providers' && (
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">
                      Providers in {agencyName}
                    </h2>

                    {/* Specialty filter dropdown */}
                    {specialtyOptions.length > 0 && (
                      <div className="mb-5">
                        <FormControl size="small" sx={{ minWidth: 260 }}>
                          <Select
                            displayEmpty
                            value={selectedSpecialty}
                            onChange={e => handleSpecialtyChange(e.target.value)}
                            sx={{
                              borderRadius: '10px',
                              fontSize: '0.875rem',
                              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e7eb' },
                              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#3BAE77' },
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#3BAE77' },
                            }}
                          >
                            <MenuItem value="">All Specialties</MenuItem>
                            {specialtyOptions.map(s => (
                              <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        {/* Selected specialty chip */}
                        {selectedSpecialty && (
                          <div className="mt-3">
                            <Chip
                              label={specialtyOptions.find(s => s.id === selectedSpecialty)?.name ?? ''}
                              onDelete={() => handleSpecialtyChange('')}
                              sx={{
                                bgcolor: '#e8f5ee',
                                color: '#1a6b3c',
                                fontWeight: 600,
                                fontSize: '0.78rem',
                                '& .MuiChip-deleteIcon': { color: '#3BAE77', '&:hover': { color: '#1a6b3c' } },
                              }}
                            />
                          </div>
                        )}
                      </div>
                    )}

                    {providers.length === 0 ? (
                      <p className="text-gray-500 text-center py-8">
                        {selectedSpecialty
                          ? 'No providers found for the selected specialty.'
                          : 'No providers listed for this agency.'}
                      </p>
                    ) : (
                      <>
                        <div className="space-y-3">
                          {providers.map((item) => (
                            <ProviderCard key={item._id} item={item} />
                          ))}
                        </div>
                        {totalProviders > providerRowsPerPage && (
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3, pt: 3, borderTop: '1px solid', borderColor: 'grey.200' }}>
                            <AdvancedPagination
                              count={totalProviders}
                              page={providerPage}
                              rowsPerPage={providerRowsPerPage}
                              onPageChange={handleProviderPageChange}
                              onRowsPerPageChange={handleProviderRowsPerPageChange}
                              rowsPerPageOptions={[10, 25, 50]}
                            />
                          </Box>
                        )}
                      </>
                    )}
                  </div>
                )}

                {/* Services tab — uses agency-level services from the get-agency response */}
                {activeTab === 'services' && (
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">Services</h2>
                    {agencyServices.length === 0 ? (
                      <p className="text-gray-500 text-center py-8">No services listed.</p>
                    ) : (
                      <div className="space-y-3">
                        {agencyServices.map((svc: any) => (
                          <div
                            key={svc._id}
                            className="p-4 border border-gray-100 rounded-xl flex justify-between items-start"
                          >
                            <div className="flex-1 min-w-0 pr-4">
                              <p className="font-semibold text-gray-900">{svc.name}</p>
                              <p className="font-semibold text-gray-900"> Provider :{svc.therapist_id?.firstName}</p>
                              {svc.specialization?.name && (
                                <p className="text-xs text-[#3BAE77] mt-0.5">🏥 {svc.specialization.name}</p>
                              )}
                              {svc.short_description && (
                                <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                                  {svc.short_description}
                                </p>
                              )}
                              <div className="flex gap-3 mt-2 flex-wrap">
                                {svc.session_length && (
                                  <span className="text-xs text-gray-400">⏱ {svc.session_length} mins</span>
                                )}
                                {svc.modality?.length > 0 && (
                                  <span className="text-xs text-gray-400 capitalize">
                                    📍 {svc.modality.join(', ').replace(/_/g, ' ')}
                                  </span>
                                )}
                              </div>
                            </div>
                            {svcFee(svc) > 0 && (
                              <span className="font-bold text-[#3BAE77] shrink-0 text-lg">
                                ${svcFee(svc)}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Reviews tab */}
                {activeTab === 'reviews' && (
                  <div className="text-center py-12 text-gray-500">
                    No reviews yet.
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right: Book a service */}
          <div className="w-full lg:w-[380px] shrink-0">
            <div className="bg-white rounded-2xl shadow-md p-6 sticky top-6">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Book a service</h2>

              {providers.length === 0 ? (
                <p className="text-sm text-gray-500">No providers available for booking.</p>
              ) : !selectedProvider ? (
                <p className="text-sm text-gray-500">Select a provider from the list to book a session.</p>
              ) : (
                <>
                  {/* Provider + price header */}
                  <div className="flex items-start justify-between mb-5 pb-4 border-b border-gray-100">
                    <p className="text-sm text-gray-600 leading-relaxed pr-3">
                      Book Session with{' '}
                      <span className="font-bold text-gray-900">{providerName}</span>
                      {' '}via{' '}
                      <span className="font-bold text-gray-900">{agencyName}</span>
                    </p>
                    {selectedPackage && (
                      <div className="text-right shrink-0">
                        <p className="text-2xl font-bold text-gray-900">${svcFee(selectedPackage)}</p>
                        <p className="text-xs text-gray-500">{selectedPackage.session_length} minute session</p>
                      </div>
                    )}
                  </div>

                  {/* Package selection */}
                  <div className="mb-5">
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-xs text-gray-500 flex items-center gap-1"> Select Package</label>
                      {selectedPackage && (
                        <button
                          onClick={() => setPackageDetailsOpen(true)}
                          className="text-xs text-[#3BAE77] font-semibold hover:underline cursor-pointer"
                        >
                          View Your Selected Package
                        </button>
                      )}
                    </div>

                    {servicesLoading ? (
                      <div className="flex justify-center py-4">
                        <CircularProgress size={24} sx={{ color: '#3BAE77' }} />
                      </div>
                    ) : providerServices.length === 0 ? (
                      <p className="text-xs text-gray-400 py-2">No packages available for this provider.</p>
                    ) : (
                      <div className="flex gap-3 overflow-x-auto pb-2">
                        {providerServices.map((svc: any) => {
                          const isPkgSelected = selectedPackage?._id === svc._id
                          const fee = svcFee(svc)
                          return (
                            <div
                              key={svc._id}
                              onClick={() => setSelectedPackage(svc)}
                              className={`cursor-pointer border min-w-[180px] py-3 px-4 rounded-xl text-sm flex flex-col transition-all shrink-0 ${isPkgSelected
                                ? 'bg-green-50 border-[#3BAE77] shadow-sm'
                                : 'bg-white border-gray-200 hover:bg-gray-50'
                                }`}
                            >
                              <div className="flex items-center justify-between mb-1">
                                <p className="font-bold text-gray-900">
                                  ${fee}{' '}
                                  <span className="text-xs font-normal text-gray-500">
                                    {svc.session_length} mins
                                  </span>
                                </p>
                                {isPkgSelected && <span className="text-[#3BAE77] font-bold">✓</span>}
                              </div>
                              <p className="text-xs text-gray-700 font-medium">{svc.name}</p>
                              <p className="text-xs text-gray-400 mt-1">🏥 In-Person</p>
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </div>

                  <button
                    disabled={!selectedPackage}
                    onClick={() => {
                      if (!selectedPackage) return
                      if (!isPatient) {
                        setLoginDialogOpen(true)
                      } else {
                        setShowBooking(true)
                      }
                    }}
                    className={`w-full py-3.5 rounded-xl text-sm font-bold transition-all ${selectedPackage
                      ? 'bg-[#3BAE77] text-white hover:bg-[#26a65b] cursor-pointer'
                      : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      }`}
                  >
                    Book Session
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Package details dialog */}
      <Dialog
        open={packageDetailsOpen}
        onClose={() => setPackageDetailsOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{ sx: { borderRadius: isMobile ? 0 : 3, maxHeight: '90vh' } }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pb: 2,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 40, height: 40, borderRadius: 2,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                bgcolor: alpha(theme.palette.primary.main, 0.12), color: 'primary.main',
              }}
            >
              <InfoIcon />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Service Package Details</Typography>
              <Typography variant="caption" color="text.secondary">Complete information about this service</Typography>
            </Box>
          </Box>
          <IconButton
            onClick={() => setPackageDetailsOpen(false)}
            size="small"
            sx={{ color: 'text.secondary', '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.1), color: 'error.main' } }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          {selectedPackage ? (
            <Box sx={{ p: 3 }}>
              <Stack spacing={3}>
                {/* Header */}
                <Box
                  sx={{
                    p: 3, borderRadius: 2,
                    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.04)} 100%)`,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  }}
                >
                  <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, color: 'primary.main' }}>
                    {selectedPackage.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                    {selectedPackage.short_description || 'No description available'}
                  </Typography>
                </Box>

                {/* Long description */}
                {selectedPackage.long_description && (
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                      <DescriptionIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary' }}>Detailed Description</Typography>
                    </Box>
                    <Paper variant="outlined" sx={{ p: 2.5, bgcolor: alpha(theme.palette.background.paper, 0.6) }}>
                      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8, whiteSpace: 'pre-line' }}>
                        {selectedPackage.long_description}
                      </Typography>
                    </Paper>
                  </Box>
                )}

                {/* Basic Information */}
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>Basic Information</Typography>
                  <Grid container spacing={2}>
                    {svcFee(selectedPackage) > 0 && (
                      <Grid item xs={12} sm={6}>
                        <Paper variant="outlined" sx={{ p: 2, height: '100%', bgcolor: alpha(theme.palette.success.main, 0.04), borderColor: alpha(theme.palette.success.main, 0.2) }}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 1, display: 'block' }}>Session Fee</Typography>
                          <Typography variant="h5" sx={{ fontWeight: 700, color: 'success.main' }}>${svcFee(selectedPackage)}</Typography>
                          <Typography variant="caption" color="text.secondary">per session</Typography>
                        </Paper>
                      </Grid>
                    )}
                    {selectedPackage.session_length && (
                      <Grid item xs={12} sm={6}>
                        <Paper variant="outlined" sx={{ p: 2, height: '100%', bgcolor: alpha(theme.palette.secondary.main, 0.04), borderColor: alpha(theme.palette.secondary.main, 0.2) }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <ScheduleIcon sx={{ fontSize: 18, color: 'secondary.main' }} />
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Session Length</Typography>
                          </Box>
                          <Typography variant="body1" sx={{ fontWeight: 600, color: 'secondary.main' }}>{selectedPackage.session_length} mins</Typography>
                        </Paper>
                      </Grid>
                    )}
                    {selectedPackage.session_buffer != null && (
                      <Grid item xs={12} sm={6}>
                        <Paper variant="outlined" sx={{ p: 2, height: '100%', bgcolor: alpha(theme.palette.info.main, 0.04), borderColor: alpha(theme.palette.info.main, 0.2) }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <AccessTimeIcon sx={{ fontSize: 18, color: 'info.main' }} />
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Session Buffer</Typography>
                          </Box>
                          <Typography variant="body1" sx={{ fontWeight: 600, color: 'info.main' }}>{selectedPackage.session_buffer} mins</Typography>
                        </Paper>
                      </Grid>
                    )}
                    {selectedPackage.language && selectedPackage.language.length > 0 && (
                      <Grid item xs={12}>
                        <Paper variant="outlined" sx={{ p: 2, bgcolor: alpha(theme.palette.success.main, 0.04), borderColor: alpha(theme.palette.success.main, 0.2) }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                            <LanguageIcon sx={{ fontSize: 18, color: 'success.main' }} />
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Languages Offered</Typography>
                          </Box>
                          <Stack direction="row" spacing={1} flexWrap="wrap">
                            {selectedPackage.language.map((lang: string, idx: number) => (
                              <Chip key={idx} label={lang} size="small" sx={{ bgcolor: alpha(theme.palette.success.main, 0.12), color: 'success.main', fontWeight: 600, mb: 1 }} />
                            ))}
                          </Stack>
                        </Paper>
                      </Grid>
                    )}
                    {selectedPackage.service_radius && (
                      <Grid item xs={12} sm={6}>
                        <Paper variant="outlined" sx={{ p: 2, bgcolor: alpha(theme.palette.info.main, 0.04), borderColor: alpha(theme.palette.info.main, 0.2) }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <RoomIcon sx={{ fontSize: 18, color: 'info.main' }} />
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Service Radius</Typography>
                          </Box>
                          <Typography variant="body1" sx={{ fontWeight: 600, color: 'info.main' }}>{selectedPackage.service_radius} miles</Typography>
                        </Paper>
                      </Grid>
                    )}
                  </Grid>
                </Box>


                {/* Tags */}
                {selectedPackage.tags && selectedPackage.tags.length > 0 && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Tags</Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
                      {selectedPackage.tags.map((tag: string, idx: number) => (
                        <Chip key={idx} label={tag} size="small" sx={{ bgcolor: alpha(theme.palette.info.main, 0.12), color: 'info.main', fontWeight: 600, mb: 1 }} />
                      ))}
                    </Stack>
                  </Box>
                )}

                {/* Specialization */}
                {selectedPackage.specialization && (
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5, color: 'text.primary' }}>Specialization</Typography>
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: alpha(theme.palette.primary.main, 0.04), borderColor: alpha(theme.palette.primary.main, 0.2) }}>
                      <Typography variant="body1" sx={{ fontWeight: 600, color: 'primary.main' }}>{selectedPackage.specialization.name}</Typography>
                      {selectedPackage.specialization.description && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>{selectedPackage.specialization.description}</Typography>
                      )}
                    </Paper>
                  </Box>
                )}

                {/* Insurance */}
                {selectedPackage.insurance && selectedPackage.insurance.length > 0 && (
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5, color: 'text.primary' }}>Insurance Providers</Typography>
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: alpha(theme.palette.primary.main, 0.04), borderColor: alpha(theme.palette.primary.main, 0.2) }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                        <HealthAndSafetyIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>Accepted Insurance</Typography>
                      </Box>
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        {selectedPackage.insurance.map((ins: any, idx: number) => (
                          <Chip key={ins._id || idx} label={ins.payer_name} size="small" sx={{ bgcolor: alpha(theme.palette.primary.main, 0.12), color: 'primary.main', fontWeight: 500 }} />
                        ))}
                      </Stack>
                    </Paper>
                  </Box>
                )}
              </Stack>
            </Box>
          ) : (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">Package details are not available.</Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`, bgcolor: alpha(theme.palette.background.paper, 0.6) }}>
          <Button onClick={() => setPackageDetailsOpen(false)} variant="contained" sx={{ fontWeight: 600 }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Login Required Dialog */}
      <Dialog
        open={loginDialogOpen}
        onClose={() => setLoginDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.12)' } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 3, position: 'relative', px: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 500, color: 'grey.700' }}>
            Login Required to Book Session
          </Typography>
          <IconButton
            onClick={() => setLoginDialogOpen(false)}
            sx={{ position: 'absolute', right: 24, top: 24, color: '#26a65b', '&:hover': { bgcolor: 'rgba(38, 166, 91, 0.08)' } }}
          >
            <CloseIcon sx={{ fontSize: '1rem' }} />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ px: 3, py: 2 }}>
          {agency && (
            <Box sx={{ mb: 3, p: 2.5, borderRadius: 2, bgcolor: 'rgba(38, 166, 91, 0.04)', border: '1px solid rgba(38, 166, 91, 0.15)' }}>
              <Typography variant="overline" sx={{ color: 'grey.500', letterSpacing: 1, fontWeight: 600 }}>Agency</Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'grey.900', mb: 1 }}>{agencyName}</Typography>
              {selectedProvider && (
                <Typography variant="body2" sx={{ color: 'grey.600' }}>
                  Provider: <strong>{providerName}</strong>
                </Typography>
              )}
            </Box>
          )}
          <Paper elevation={0} sx={{ p: 4, bgcolor: 'grey.50', borderRadius: 2, border: '1px solid', borderColor: 'grey.200', textAlign: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'grey.800', mb: 1 }}>
              Login Required to Book Session
            </Typography>
            <Typography variant="body2" sx={{ color: 'grey.600', lineHeight: 1.6, mb: 3 }}>
              Please log in to your patient account or create a new account to book a session and access all booking features.
            </Typography>
            <Button
              variant="contained"
              startIcon={<PersonIcon />}
              onClick={() => navigate('/patient-login', { state: { returnTo: window.location.pathname } })}
              sx={{
                bgcolor: '#26a65b',
                color: 'white',
                borderRadius: 2,
                px: 3,
                py: 1,
                fontWeight: 600,
                '&:hover': { bgcolor: '#1e8a4c', transform: 'translateY(-2px)', boxShadow: '0 8px 25px rgba(38, 166, 91, 0.3)' },
              }}
            >
              Sign Up / Login
            </Button>
          </Paper>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setLoginDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {showBooking && selectedProvider && selectedPackage && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm z-[9999]">
          <AgencyBooking
            therapistId={selectedProvider._id}
            therapistData={{
              image: selectedProvider.profilePhotoUrl || '/agencyImage/male-dummy-user.png',
              name: `${selectedProvider.firstName || ''} ${selectedProvider.lastName || ''}`.trim(),
              experience: selectedProvider.yearsOfExperience
                ? `${selectedProvider.yearsOfExperience} years`
                : '',
              rate: `$${svcFee(selectedPackage)}`,
              id: selectedProvider._id,
              firstName: selectedProvider.firstName,
              lastName: selectedProvider.lastName,
              avatarUrl: selectedProvider.profilePhotoUrl || '/agencyImage/male-dummy-user.png',
              specialties:selectedProvider.specialization.map((item: any) => item.name),
              insurances:selectedProvider.providerInsurance.map((item: any) => item?.payer_id?.payer_name),
            }}
            selectedPackage={{
              id: selectedPackage._id,
              name: selectedPackage.name,
              rate: `$${svcFee(selectedPackage)}`,
              duration: `${selectedPackage.session_length} min`,
              type: 'In-Person',
            }}
            agencyId={agencyId}
            agencyName={agencyName}
            defaultDate={null}
            onClose={() => setShowBooking(false)}
            onComplete={() => setShowBooking(false)}
          />
        </div>
      )}
    </MainLayout>
  )
}

export default OpenAgencyPage
